import {
  ArrowDropDown,
  ChatBubble,
  Email,
  Public,
  Smartphone,
  Notifications,
  Check,
  Close,
} from '@material-ui/icons';
import { keyBy, mapValues, flatMapDeep } from 'lodash';
import {
  NotificationSubscription,
  NotificationChannel,
  NotificationChannelTableColumn,
  NotificationChannelType,
  NotificationEventCategory,
  NotificationEvent,
  ScheduleType,
  NotificationStepDefinition,
  UINotificationStepDefinition,
} from 'models';
import React, {
  ReactElement,
  ReactNode,
  useEffect,
  useMemo,
  useState,
  useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store/reducers';
import {
  getSubscriptions,
  addSubscription,
  unsubscribe,
  subscribeStep,
  unsubscribeStep,
} from 'store/actions/organizations/users/notifications';
import {
  AutoSizer,
  Column,
  Index,
  Table,
  TableRowProps,
} from 'react-virtualized';

import './UserNotificationsView.scss';
import {
  Button,
  FormControl,
  FormControlLabel,
  MenuItem,
  Select,
  Switch,
} from '@material-ui/core';
import LoadingIndicator from 'components/shared/LoadingIndicator';

import 'react-virtualized/styles.css';

const NOTIFICATION_CHANNELS: NotificationChannelTableColumn[] = [
  {
    key: NotificationChannelType.PERSIST,
    title: 'Persistent',
    icon: Notifications,
    enabled: false,
  },
  {
    key: NotificationChannelType.EMAIL_TRANS,
    title: 'Email',
    icon: Email,
    enabled: true,
  },
  {
    key: NotificationChannelType.MOBILE_SMS,
    title: 'SMS',
    icon: ChatBubble,
    enabled: true,
  },
  {
    key: NotificationChannelType.MOBILE_PUSH,
    title: 'Mobile Push',
    icon: Smartphone,
    enabled: true,
  },
  {
    key: NotificationChannelType.WEB_PUSH,
    title: 'Web Push',
    icon: Public,
    enabled: true,
  },
];

interface UserNotificationsViewProps {
  organizationId: string;
  userId: string;
}

const UserNotificationsView = ({
  organizationId,
  userId,
}: UserNotificationsViewProps): ReactElement => {
  const dispatch = useDispatch();

  const { notifications: userNotifications } = useSelector(
    ({ organizations: { users } }: RootState) => ({ ...users })
  );

  const initialLoadingNotifications = useMemo(
    () =>
      mapValues(keyBy(userNotifications.data.subscriptions, 'eventId'), () => ({
        channels: [],
        stepDefinitions: [],
      })),
    [userNotifications.data]
  );

  const [
    [loadingNotifications, setLoadingNotifications],
    [showIndividualStepSubscriptions, setShowIndividualStepSubscriptions],
    notificationsTable,
  ] = [
    useState<{
      [key: string]: {
        channels: NotificationChannel['id'][];
        stepDefinitions: NotificationStepDefinition['id'][];
      };
    }>(initialLoadingNotifications),
    useState(false),
    useRef<Table>(null),
  ];

  useEffect(() => {
    setLoadingNotifications(initialLoadingNotifications);
  }, [initialLoadingNotifications, setLoadingNotifications]);

  const subscriptionMap = useMemo(
    () =>
      keyBy(
        userNotifications.data.subscriptions,
        ({ eventId, channelId }) => `${eventId}-${channelId}`
      ),
    [userNotifications.data]
  );

  const tableColumns: NotificationChannelTableColumn[] = [
    {
      title: 'Type',
      key: 'type',
      enabled: true,
    },
    ...NOTIFICATION_CHANNELS,
  ];

  const allSteps = useMemo(
    () =>
      flatMapDeep(userNotifications.data.stepDefinitions, (stepDefinition) =>
        stepDefinition.childSteps
          ? [
              stepDefinition,
              ...stepDefinition.childSteps.map((step) => ({
                ...step,
                level: 1,
              })),
            ]
          : stepDefinition
      ),
    [userNotifications.data]
  );

  const tableData: Array<
    NotificationEventCategory | NotificationEvent | UINotificationStepDefinition
  > = useMemo(
    () =>
      userNotifications.data.eventCategories.flatMap((category) => [
        category,
        ...category.events.flatMap((event) => {
          if (!event.isStepEvent || !showIndividualStepSubscriptions)
            return event;

          return [
            event,
            ...allSteps.map((step) => ({ ...step, eventId: event.id })),
          ];
        }),
      ]),
    [
      userNotifications.data.eventCategories,
      showIndividualStepSubscriptions,
      allSteps,
    ]
  );

  useEffect(() => {
    dispatch(
      getSubscriptions({
        orgId: organizationId!,
        userId: userId!,
      })
    );
  }, [dispatch, organizationId, userId]);

  const updateSubscription = async (subscription: NotificationSubscription) => {
    setLoadingNotifications((previousLoadingNotifications) => ({
      ...previousLoadingNotifications,
      [subscription.eventId]: {
        ...previousLoadingNotifications[subscription.eventId],
        channels: [
          ...previousLoadingNotifications[subscription.eventId].channels,
          subscription.channelId,
        ],
      },
    }));

    try {
      if (subscription.scheduleType === 'DISABLED') {
        await dispatch(
          unsubscribe({
            orgId: organizationId!,
            userId: userId!,
            eventId: subscription.eventId,
            channelId: subscription.channelId,
          })
        );
      } else {
        await dispatch(
          addSubscription({
            orgId: organizationId!,
            userId: userId!,
            subscription,
          })
        );
      }
    } finally {
      setLoadingNotifications((previousLoadingNotifications) => ({
        ...previousLoadingNotifications,
        [subscription.eventId]: {
          ...previousLoadingNotifications[subscription.eventId],
          channels: previousLoadingNotifications[
            subscription.eventId
          ].channels.filter(
            (channelId) => subscription.channelId !== channelId
          ),
        },
      }));
    }
  };

  const toggleIndividualStepSubscriptions = () => {
    setShowIndividualStepSubscriptions((previous) => !previous);
  };

  const updateIndividualStepSubscription = async (
    stepDefinition: UINotificationStepDefinition,
    subscribed: boolean
  ) => {
    setLoadingNotifications((previousLoadingNotifications) => ({
      ...previousLoadingNotifications,
      [stepDefinition.eventId]: {
        ...previousLoadingNotifications[stepDefinition.eventId],
        stepDefinitions: [
          ...previousLoadingNotifications[stepDefinition.eventId]
            .stepDefinitions,
          stepDefinition.id,
        ],
      },
    }));

    try {
      const payload = {
        orgId: organizationId!,
        userId: userId!,
        eventId: stepDefinition.eventId,
        stepDefinitionId: stepDefinition.id,
        subscribed,
      };

      if (subscribed) await dispatch(subscribeStep(payload));
      else await dispatch(unsubscribeStep(payload));
    } finally {
      setLoadingNotifications((previousLoadingNotifications) => ({
        ...previousLoadingNotifications,
        [stepDefinition.eventId]: {
          ...previousLoadingNotifications[stepDefinition.eventId],
          stepDefinitions: previousLoadingNotifications[
            stepDefinition.eventId
          ].stepDefinitions.filter(
            (stepDefinitionId) => stepDefinition.id !== stepDefinitionId
          ),
        },
      }));
    }
  };

  const notificationScheduleRow = ({
    style,
    rowData,
  }: TableRowProps): ReactNode => {
    const eventCategory =
      'events' in rowData ? (rowData as NotificationEventCategory) : undefined;
    if (eventCategory) {
      return (
        <div
          className="ReactVirtualized__Table__row NotificationTypeSchedule__typeContainer padding-sm"
          style={style}
          key={eventCategory.type}
        >
          <Button startIcon={<ArrowDropDown />}>
            <b>{eventCategory.name}</b>
          </Button>
        </div>
      );
    }

    if ('isStepEvent' in rowData) {
      const event = rowData as NotificationEvent;

      return (
        <div
          className="ReactVirtualized__Table__row"
          style={style}
          key={event.id}
        >
          <span className="ReactVirtualized__Table__rowColumn NotificationTypeSchedule__typeContainer flex-rows">
            <span>{event.title}</span>
            {event.isStepEvent && (
              <button
                type="button"
                className="ShowIndividualButton"
                onClick={toggleIndividualStepSubscriptions}
              >
                {showIndividualStepSubscriptions ? 'Hide' : 'Show'} Individual
                Steps
              </button>
            )}
          </span>

          {userNotifications.data.channels?.map((channel) => {
            const subscription = subscriptionMap[
              `${event.id}-${channel.id}`
            ] ?? {
              eventId: event.id,
              channelId: channel.id,
              scheduleType: ScheduleType.DEFAULT,
              schedule: null,
            };

            let selectedValue = 'off';
            switch (subscription.scheduleType) {
              case ScheduleType.DISABLED:
                selectedValue = 'off';
                break;
              case ScheduleType.CUSTOM:
                selectedValue = 'custom';
                break;
              case ScheduleType.DEFAULT:
              default:
                selectedValue = 'on';
                break;
            }

            const notificationIsLoading = loadingNotifications[
              event.id
            ]?.channels.includes(subscription.channelId);

            return (
              <span
                key={`${channel.id}-${channel.name}`}
                className="NotificationTypeSchedule__selectContainer center-align align-center ReactVirtualized__Table__rowColumn padding-left-sm padding-right-sm"
              >
                <FormControl variant="outlined" size="small" fullWidth>
                  <Select
                    disabled={notificationIsLoading}
                    IconComponent={() => {
                      if (notificationIsLoading) {
                        return <LoadingIndicator size={24} />;
                      }

                      const Icon = selectedValue === 'off' ? Close : Check;
                      const color =
                        selectedValue === 'off' ? '#888' : '#29a14f';

                      return (
                        <Icon
                          className="NotificationsSubscriptionSettingIcon"
                          style={{ color }}
                        />
                      );
                    }}
                    value={selectedValue}
                    onChange={({ target: { value } }) =>
                      updateSubscription({
                        ...subscription,
                        scheduleType:
                          value === 'on'
                            ? ScheduleType.DEFAULT
                            : ScheduleType.DISABLED,
                      })
                    }
                  >
                    <MenuItem value="off">Off</MenuItem>
                    <MenuItem value="on">On</MenuItem>
                  </Select>
                </FormControl>
              </span>
            );
          })}
        </div>
      );
    }

    const stepDefinition = rowData as UINotificationStepDefinition;
    const isSubscribed =
      userNotifications.data.subscribedStepDefinitionIds.includes(
        stepDefinition.id
      );
    const stepDefinitionIsLoading = loadingNotifications[
      stepDefinition.eventId
    ]?.stepDefinitions.includes(stepDefinition.id);

    return (
      <div
        className="ReactVirtualized__Table__row padding-left-lg padding-right-lg"
        key={stepDefinition.id}
        style={style}
      >
        <span
          className="flex-grow"
          style={{ paddingLeft: `${10 * (stepDefinition.level ?? 0)}px` }}
        >
          {stepDefinition.name}
        </span>

        {!stepDefinition.childSteps?.length && (
          <FormControlLabel
            value="left"
            control={
              <Switch
                color="primary"
                checked={stepDefinitionIsLoading ? !isSubscribed : isSubscribed}
                onChange={() =>
                  updateIndividualStepSubscription(
                    stepDefinition,
                    !isSubscribed
                  )
                }
                name={`${stepDefinition.id}_subscriptionCheckbox`}
              />
            }
            label={stepDefinitionIsLoading && <LoadingIndicator size={20} />}
            labelPlacement="start"
          />
        )}
      </div>
    );
  };

  const getRowHeight = ({ index }: Index) => {
    const rowData = tableData[index];

    // event category
    if ('events' in rowData) return 40;

    // event
    if ('isStepEvent' in rowData) return 60;

    // step definition
    if ('childSteps' in rowData) return 30;

    return 40;
  };

  return (
    <div className="full-height">
      {userNotifications.error && <p>{userNotifications.error.message}</p>}

      {userNotifications.loading ? (
        <LoadingIndicator />
      ) : (
        <AutoSizer style={{ flex: 1 }}>
          {({ width, height }) => (
            <Table
              ref={notificationsTable}
              width={width}
              height={height}
              rowHeight={getRowHeight}
              headerHeight={40}
              rowCount={tableData.length}
              rowGetter={({ index }) => tableData[index]}
              rowRenderer={notificationScheduleRow}
            >
              {tableColumns
                .filter(({ enabled }) => enabled)
                .map((column, index) => {
                  const Icon = column.icon;
                  return (
                    <Column
                      key={`${column.title}-${column.key}`}
                      label={
                        <span className="valign-center">
                          {Icon && (
                            <Icon className="NotificationsTableColumnHeader_icon" />
                          )}
                          <span>{column.title}</span>
                        </span>
                      }
                      width={index === 0 ? 230 : 140}
                      dataKey={column.key}
                      columnData={column}
                    />
                  );
                })}
            </Table>
          )}
        </AutoSizer>
      )}
    </div>
  );
};
export default UserNotificationsView;
